import React from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { Menu, X, User, LogOut, LayoutDashboard } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Button } from '../components/ui/button';

export default function Navbar() {
  const { theme, isNeutral } = useTheme();
  const { user, isAuthenticated, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const bgClass = theme === 'men' 
    ? 'bg-zinc-950/95 border-zinc-800' 
    : theme === 'women' 
      ? 'bg-white/95 border-pink-100' 
      : 'bg-white/95 border-zinc-200';

  const textClass = theme === 'men' ? 'text-white' : 'text-zinc-900';
  const logoClass = theme === 'men' 
    ? 'text-blue-500' 
    : theme === 'women' 
      ? 'text-pink-600' 
      : 'text-zinc-900';

  const getDashboardLink = () => {
    if (!user) return '/login';
    switch (user.role) {
      case 'admin': return '/admin';
      case 'shop_owner': return '/owner';
      default: return '/dashboard';
    }
  };

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 glass ${bgClass} border-b`} data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className={`text-2xl font-bold ${logoClass} tracking-tight`} data-testid="logo">
            <span className={theme === 'women' ? 'font-heading-women italic' : 'font-heading-men'}>
              Zenvy
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <Link 
              to="/salons/men" 
              className={`${textClass} hover:opacity-70 transition-opacity font-medium`}
              data-testid="nav-men"
            >
              Men's Salon
            </Link>
            <Link 
              to="/salons/women" 
              className={`${textClass} hover:opacity-70 transition-opacity font-medium`}
              data-testid="nav-women"
            >
              Women's Salon
            </Link>
            <Link 
              to="/about" 
              className={`${textClass} hover:opacity-70 transition-opacity font-medium`}
              data-testid="nav-about"
            >
              About
            </Link>
            <Link 
              to="/contact" 
              className={`${textClass} hover:opacity-70 transition-opacity font-medium`}
              data-testid="nav-contact"
            >
              Contact
            </Link>
          </div>

          {/* Auth Section */}
          <div className="hidden md:flex items-center space-x-4">
            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    className={`flex items-center gap-2 ${textClass}`}
                    data-testid="user-menu-trigger"
                  >
                    {user.picture ? (
                      <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
                    ) : (
                      <User className="w-5 h-5" />
                    )}
                    <span className="max-w-[100px] truncate">{user.name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-medium">{user.name}</p>
                    <p className="text-xs text-muted-foreground">{user.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to={getDashboardLink()} className="flex items-center gap-2" data-testid="nav-dashboard">
                      <LayoutDashboard className="w-4 h-4" />
                      Dashboard
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} className="flex items-center gap-2 text-red-600" data-testid="logout-btn">
                    <LogOut className="w-4 h-4" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" className={textClass} data-testid="login-btn">
                    Login
                  </Button>
                </Link>
                <Link to="/register">
                  <Button 
                    className={theme === 'men' 
                      ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                      : theme === 'women'
                        ? 'bg-pink-600 hover:bg-pink-700 text-white'
                        : 'bg-zinc-900 hover:bg-zinc-800 text-white'
                    }
                    data-testid="signup-btn"
                  >
                    Sign Up
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button 
            className={`md:hidden ${textClass}`}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className={`md:hidden ${bgClass} border-t ${theme === 'men' ? 'border-zinc-800' : 'border-zinc-200'}`}>
          <div className="px-4 py-4 space-y-3">
            <Link 
              to="/salons/men" 
              className={`block py-2 ${textClass}`}
              onClick={() => setMobileMenuOpen(false)}
            >
              Men's Salon
            </Link>
            <Link 
              to="/salons/women" 
              className={`block py-2 ${textClass}`}
              onClick={() => setMobileMenuOpen(false)}
            >
              Women's Salon
            </Link>
            <Link 
              to="/about" 
              className={`block py-2 ${textClass}`}
              onClick={() => setMobileMenuOpen(false)}
            >
              About
            </Link>
            <Link 
              to="/contact" 
              className={`block py-2 ${textClass}`}
              onClick={() => setMobileMenuOpen(false)}
            >
              Contact
            </Link>
            <div className="pt-4 border-t border-zinc-700">
              {isAuthenticated ? (
                <>
                  <Link 
                    to={getDashboardLink()} 
                    className={`block py-2 ${textClass}`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Dashboard
                  </Link>
                  <button 
                    onClick={() => { logout(); setMobileMenuOpen(false); }}
                    className="block py-2 text-red-500"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <Link 
                    to="/login" 
                    className={`block py-2 ${textClass}`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Login
                  </Link>
                  <Link 
                    to="/register" 
                    className={`block py-2 ${textClass}`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Sign Up
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
